import globalConfig from '@utiles/globalConfig';
import { getGlobalGetGotoLink } from '@utiles/globalGetLink';
import { getSupportThemeClass } from '@utiles/officialSupport/theme';
import { isSupportCenterV2 } from './supportCenterVersion';

export {
  SUPPORT_CENTER_VERSION,
  SUPPORT_CENTER_VERSION_STORAGE_KEY,
  isForceSupportCenterV2ByUrl,
  isSupportCenterV2,
  normalizeSupportCenterVersion,
  readCachedSupportCenterVersion,
  readInitialSupportCenterVersion,
  persistSupportCenterVersion,
  resetSupportCenterVersionCache,
  shouldPassMockToAccount,
  getSupportCenterApiUrl,
  getSupportCenterAuthHeaders,
  buildSupportCenterVersionParams
} from './supportCenterVersion';

// 邮箱正则：后台也用这个正则校验
export const SUPPORT_EMAIL_REG =
  /^([a-zA-Z0-9_-])+([a-zA-Z0-9_.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;

// 格式化特殊游戏显示
export function formatGameName(gameId, gameName) {
  const MAIN_HOST = globalConfig.getMainHost();
  if (gameId === 195 && MAIN_HOST === atob('ZW5kb21haW5uYW1lLmNvbQ==')) {
    return 'Soul Land ตำนานจอมยุทธ์ภูตถังซาน';
  }
  return gameName;
}

// 邮箱格式校验
export function validSupportEmail(email) {
  SUPPORT_EMAIL_REG.lastIndex = 0;
  return SUPPORT_EMAIL_REG.test(email);
}

// 2进制转16进制
export function bin2hex(str) {
  var ret = '';
  var r = /[0-9a-zA-Z_.~!*()]/;
  for (var i = 0, l = str.length; i < l; i++) {
    if (r.test(str.charAt(i))) {
      ret += str.charCodeAt(i).toString(16);
    } else {
      ret += encodeURIComponent(str.charAt(i)).replace(/%/g, '');
    }
  }
  return ret;
}

// 16进制转2进制
export function hex2bin(str) {
  var ret = '';
  var tmp = '';
  for (var i = 0; i < str.length - 1; i += 2) {
    var c = String.fromCharCode(parseInt(str.substr(i, 2), 16));
    if (c.charCodeAt() > 127) {
      tmp += '%' + str.substr(i, 2);
      if (tmp.length == 9) {
        ret += decodeURIComponent(tmp);
        tmp = '';
      }
    } else {
      ret += c;
    }
  }
  return ret;
}

// 判断一个时间戳是否是今天
export function isToday(timeStamp) {
  // 创建 Date 对象，传入时间戳参数
  const date = new Date(timeStamp);
  // 获取当前日期
  const today = new Date();
  // 判断日期是否相等
  if (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  ) {
    return true;
  } else {
    return false;
  }
}

/** AI Bot V2 嵌入人工工单：form_type + inIframe 双条件命中 */
export function isHumanSupportTicketEmbed() {
  return (
    globalConfig.getHrefParam('form_type') === 'human_support_ticket' &&
    globalConfig.getHrefParam('inIframe') === 'service_aibot_v2'
  );
}

/**
 * 工单版本：与页面级 support_center_version 对齐（create_request_v2 + popupForm）
 * 页面级非 V2 时一律 V1，不影响旧入口 / AI 直开 worktemp
 * 注意：详情/回复/评价的 order_version 应以列表 ORDER_VERSION 为准，勿用本方法兜底
 */
export const getSupportOrderVersion = () => (isSupportCenterV2() ? 2 : 1);

export const isSupportOrderV2 = () => isSupportCenterV2();

/**
 * 规范化工单 order_version：仅接受 1/2，其它返回 undefined（调用方不传该字段）
 */
export const normalizeOrderVersion = value => {
  const n = Number(value);
  if (n === 1 || n === 2) {
    return n;
  }
  return undefined;
};

/**
 * 从路由 query 读取有效 order_version（无则 undefined）
 */
export const getOrderVersionFromRouteQuery = (query = {}) =>
  normalizeOrderVersion(query?.order_version);

/**
 * 列表进详情：有 ORDER_VERSION 时写入 query.order_version，否则剔除避免误传
 */
export const buildMyWorkQuery = (baseQuery = {}, workItem = {}) => {
  const next = { ...baseQuery };
  const orderVersion = normalizeOrderVersion(
    workItem?.ORDER_VERSION ?? workItem?.order_version
  );
  if (orderVersion != null) {
    next.order_version = String(orderVersion);
  } else {
    delete next.order_version;
  }
  return next;
};

export const getOrderSourceParams = () => {
  let sourceParams = { front_source_type: '', detail_source_id: '' };
  // 新客服中心：统一 front_source_type=support_center
  if (isSupportCenterV2()) {
    sourceParams.front_source_type = 'support_center';
    return sourceParams;
  }
  let customFrom = globalConfig.getHrefParam('customFrom');
  const packageName = globalConfig.getHrefParam('packageName');
  const sdkVersionName = globalConfig.getHrefParam('sdkVersionName');
  const packageVersion = globalConfig.getHrefParam('packageVersion');
  const sdkVersion = globalConfig.getHrefParam('sdkVersion');
  customFrom = customFrom?.split('-')?.[1] || '';
  const sourceType = {
    login: 'login',
    passport: 'userCenter',
    support: 'support',
    auto: 'activity',
    event: 'activity',
    h5: 'h5',
    pay: 'pay',
    website: 'website',
    sdk: 'sdk',
    aigs: 'aigs',
    exe: 'exe',
  };
  if (sourceType[customFrom]) {
    sourceParams.front_source_type = sourceType[customFrom];
  } else if (packageName && (sdkVersionName || sdkVersion || packageVersion)) {
    sourceParams.front_source_type = 'sdk';
  }
  return sourceParams;
};

/**
 * 组装智能工单路由 query：客服中心 V2 时附带 order_version=2（便于链路识别）
 */
export const buildWorkTempQuery = (query = {}) => {
  const next = { ...query };
  if (isSupportCenterV2()) {
    next.order_version = '2';
  } else {
    delete next.order_version;
  }
  return next;
};

/**
 * 智能客服落地链接（域名不写死）
 * - 客服中心 V2：service.{host}/aiGameBuddy/chat-app-support/index.html
 * - 旧入口：gsupport.{host}/smartChat/aibot（保持现网）
 * refer：URL 有则透传，无则不带
 * @param {{
 *   gameId?: string|number,
 *   language?: string,
 *   serverId?: string|number,
 *   roleId?: string|number,
 *   roleName?: string,
 *   refer?: string
 * }} [options]
 */
export const buildAiBotLink = (options = {}) => {
  const mainHost = globalConfig.getMainHost();
  const gameId =
    options.gameId ??
    (globalConfig.getHrefParam('gameId') ||
      globalConfig.getHrefParam('gameID') ||
      globalConfig.getHrefParam('game_id') ||
      '');
  const language =
    options.language ??
    (globalConfig.getHrefParam('language') ||
      globalConfig.getHrefParam('appLanguage') ||
      '');
  const serverId =
    options.serverId ??
    (globalConfig.getHrefParam('serverId') ||
      globalConfig.getHrefParam('server_id') ||
      '');
  const roleId =
    options.roleId ?? (globalConfig.getHrefParam('roleId') || '');
  const roleName =
    options.roleName ?? (globalConfig.getHrefParam('roleName') || '');
  const refer =
    options.refer !== undefined
      ? options.refer
      : globalConfig.getHrefParam('refer') || '';

  if (isSupportCenterV2()) {
    const params = new URLSearchParams();
    params.set('gameId', String(gameId || ''));
    params.set('language', String(language || ''));
    params.set('serverId', String(serverId || ''));
    params.set('roleId', String(roleId || ''));
    params.set('roleName', String(roleName || ''));
    if (refer) {
      params.set('refer', String(refer));
    }
    return `https://service.${mainHost}/aiGameBuddy/chat-app-support/index.html?${params.toString()}`;
  }

  return `https://gsupport.${mainHost}/smartChat/aibot?gameId=${gameId}&serverId=${serverId}&roleId=${roleId}&roleName=${roleName}&language=${language}`;
};

/**
 * gabres 自助 fixed 外链：域名不写死，透传当前页 URL 参数，覆盖 source/type/themeStyle
 * themeStyle：按 gameId 皮肤映射，仅 dark 传 dark，其余（含 roworld）传 common
 * @param {string} key smartTypeXxx
 * @param {{ language?: string }} [options]
 */
export const buildFixedSelfServiceLink = (key, options = {}) => {
  if (!key) {
    return '';
  }
  const mainHost = globalConfig.getMainHost();
  const params = new URLSearchParams();
  const hrefParams =
    (typeof globalConfig.getAllUrlParamsObj === 'function' &&
      globalConfig.getAllUrlParamsObj()) ||
    globalConfig.getHrefParamObj() ||
    {};
  Object.keys(hrefParams).forEach(name => {
    if (hrefParams[name] == null || hrefParams[name] === '') {
      return;
    }
    // hash 路由参数 / 由本函数覆盖的参数不透传
    if (
      [
        'workTempId',
        'formId',
        'smartType',
        'source',
        'type',
        'name',
        'themeStyle'
      ].includes(name)
    ) {
      return;
    }
    params.set(name, hrefParams[name]);
  });
  const gameId =
    hrefParams.gameId ||
    hrefParams.gameID ||
    hrefParams.game_id ||
    globalConfig.getHrefParam('gameId') ||
    '';
  if (gameId) {
    params.set('gameId', gameId);
  }
  const lang =
    options.language ||
    hrefParams.language ||
    hrefParams.appLanguage ||
    globalConfig.getHrefParam('language') ||
    globalConfig.getHrefParam('appLanguage') ||
    '';
  if (lang) {
    if (!params.get('language')) {
      params.set('language', lang);
    }
    if (!params.get('appLanguage')) {
      params.set('appLanguage', lang);
    }
  }
  params.set('source', 'fixed');
  params.set('type', key);
  // iframe 皮肤：dark → dark，其余（common / roworld）→ common
  const theme = getSupportThemeClass(gameId);
  params.set('themeStyle', theme === 'dark' ? 'dark' : 'common');
  const link = `https://gabres.${mainHost}/selfService/?${params.toString()}`;
  try {
    return getGlobalGetGotoLink({ link });
  } catch (e) {
    return link;
  }
};
